import { AreaCategory, ProcessTypes, UserWorkoutSession } from 'fitbeat.gym-core';
import { GymTypes, IDay, WorkoutType } from 'fitbeat.models';
import moment from 'moment-timezone';
import { createSelector } from 'reselect';
import { IAppStore } from '../HomeWorkouts';
import { GymResolver } from '../services';

const getPersonaliseSetupScreen = (state) => state.bookings.bookingWizard.currentPersonaliseSetupScreen;
const getWaitlistBookError = (state) => state.bookings.bookingWizard.bookWaitlistSlotError;
const getWaitlistBookLoading = (state) => state.bookings.bookingWizard.bookWaitlistSlotLoading;

export const personaliseSetupScreenSelector = createSelector(
  [getPersonaliseSetupScreen],
  (currentScreen) => currentScreen,
);

const getHasPersonaliseSetupWarning = (state) => state.bookings.bookingWizard.personaliseSetupWarning;

export const personaliseSetupWarningSelector = createSelector(
  [getHasPersonaliseSetupWarning],
  (warning) => warning,
);

const getIsHomeGym = (state: IAppStore) => {
  const gym = GymResolver.getByName(state.bookings.bookingWizard.workoutSession.gymName);
  const isHomeWorkout = gym.type === GymTypes.Home;
  return isHomeWorkout;
};

const getHasInvites = (state: IAppStore) => {
  const { openInviteClassesByWorkoutId } = state.bookings.bookings as any;
  const openInviteClass = openInviteClassesByWorkoutId[state.bookings.bookingWizard.workoutSession.uniqueReference];
  const hasPartners = openInviteClass ? openInviteClass.openInviteUserProfiles.length > 0 : false;
  return hasPartners;
};

export const isHomeGroupClassSelector = createSelector(
  [getIsHomeGym, getHasInvites],
  (isHomeGym, hasOpenInvites) => isHomeGym && hasOpenInvites,
);

export const isStudioPartnerBookingSelector = createSelector(
  [getIsHomeGym, getHasInvites],
  (isHomeGym, hasOpenInvites) => !isHomeGym && hasOpenInvites,
);

export const shouldShowCusomiseWorkoutSettingsInBooking = createSelector(
  [(workoutSessionInfo: { isStreamingWorkout?: boolean,
    pairedSessionReference?: string } | null) => workoutSessionInfo],
  (workoutSessionInfo) => !!workoutSessionInfo && (UserWorkoutSession.isStreamingWorkoutAndNotHost(workoutSessionInfo)
        || !workoutSessionInfo.isStreamingWorkout),
);

export const createDemoBookingScreenDataSelector = createSelector(
  [
    (state: IAppStore) => state.bookings.demo.createDemoBookingScreenError,
    (state: IAppStore) => state.bookings.demo.loadingCreateDemoData,
    (state: IAppStore) => state.bookings.demo.cannotLaunchDemoBookingScreen,
    (state: IAppStore) => state.bookings.demo.shouldNavigateToInstallAppScreen,
  ],
  (
    createDemoBookingScreenError,
    loadingCreateDemoData,
    cannotLaunchDemoBookingScreen,
    shouldNavigateToInstallAppScreen,
  ) => ({
    createDemoBookingScreenError,
    loadingCreateDemoData,
    cannotLaunchDemoBookingScreen,
    shouldNavigateToInstallAppScreen,
  }),
);

export const waitlistBookErrorSelector = createSelector(
  [getWaitlistBookError],
  (error) => error,
);

export const waitlistBookLoadingSelector = createSelector(
  [getWaitlistBookLoading],
  (isLoading) => isLoading,
);

export const currentSelectedGymSelector = createSelector(
  [
    (state: IAppStore) => state.bookings.bookingWizard.currentSelectedGymName,
  ],
  (
    currentSelectedGymName,
  ) => currentSelectedGymName && (GymResolver.getByName(currentSelectedGymName)),
);

export const isOfficeScheduleBookingSelector = createSelector(
  [
    (state: IAppStore) => state.bookings.bookingWizard.isOfficeScheduleBooking,
  ],
  (
    isOfficeScheduleBooking,
  ) => isOfficeScheduleBooking ?? false,
);

export const areaCategoriesSelector = createSelector(
  [
    (state: IAppStore) => state.bookings.bookingWizard.studioZones.map(({ areaCategory }) => areaCategory),
  ],
  (areaCategories) => areaCategories,
);

export const areaStatusSelector = createSelector(
  [
    (state: IAppStore, selectedCategory: AreaCategory) => state.bookings.bookingWizard.studioZones
      .find(({ areaCategory }) => areaCategory === selectedCategory)?.status,
  ],
  (
    (status) => status || undefined
  ),
);

export const areaWarningSelector = createSelector(
  [
    (state: IAppStore, selectedCategory: AreaCategory) => state.bookings.bookingWizard.studioZones
      .find(({ areaCategory }) => areaCategory === selectedCategory)?.warning,
  ],
  (
    (warning) => warning || undefined
  ),
);

export const timeslotDurationsSelector = createSelector(
  [
    (state: IAppStore, selectedCategory: AreaCategory) => state.bookings.bookingWizard.studioZones
      .find(({ areaCategory }) => areaCategory === selectedCategory)?.availableTimeSlots
      ?.map(({ workoutType }) => workoutType),
  ],
  (timeslotDurations) => timeslotDurations || [],
);

export const classTimesSelector = createSelector(
  [
    (
      state: IAppStore,
      selectedCategory: AreaCategory,
      selectedDuration: WorkoutType,
    ) => state.bookings.bookingWizard.studioZones
      .find(({ areaCategory }) => areaCategory === selectedCategory)?.availableTimeSlots
      ?.find(({ workoutType }) => workoutType === selectedDuration)?.classTimes,
  ],
  (classTimes) => classTimes || [],
);

const getOnDemandSlots = (state: IAppStore) => state.bookings.bookingWizard.onDemandSlots;

export const onDemandSlotsAvailableSelector = createSelector(
  getOnDemandSlots,
  (onDemandSlots) => !!(onDemandSlots) && onDemandSlots.length > 0,
);

export const onDemandSlotsSelector = createSelector(
  getOnDemandSlots,
  (onDemandSlots) => onDemandSlots,
);

export const recommendedSettingsSelector = createSelector(
  (state: IAppStore) => state.bookings.bookingWizard.recommendedSettings,
  (recommendedSettings) => recommendedSettings,
);

export const selectStartTimePrimaryActionLoading = createSelector(
  [
    (state: IAppStore) => state.bookings.bookingWizard.isLoadingBookingSlots,
    (state: IAppStore) => state.bookings.bookingWizard.recommendedSettingsLoading,
    (state: IAppStore) => state.bookings.bookingWizard.creatingDemoBookingInProgress,
    (state: IAppStore) => state.bookings.bookingWizard.waitlistLoading,
    (state: IAppStore) => state.bookings.bookingWizard.subscriptionPortalLoading,
    (state: IAppStore) => state.bookings.bookingWizard.startOnDemandBookingLoading,
  ],
  (
    isLoadingBookingSlots,
    recommendedSettingsLoading,
    creatingDemoBookingInProgress,
    waitlistLoading,
    subscriptionPortalLoading,
    startOnDemandBookingLoading,
  ) => (
    isLoadingBookingSlots
    || recommendedSettingsLoading
    || creatingDemoBookingInProgress
    || waitlistLoading
    || subscriptionPortalLoading
    || startOnDemandBookingLoading
  ),
);
